import React, { useState, useRef, useEffect } from "react";
import Map, {
  Marker,
  MapRef,
  NavigationControl,
  FullscreenControl,
} from "react-map-gl";

import "mapbox-gl/dist/mapbox-gl.css";
import { MAPBOX_ACCESS_TOKEN } from "../../../config";
import { CFormInput } from "@coreui/react";
import { RequiredIndicator } from "../FormFields";
import { Wrapper } from "../../styled/Styled";
import { TError } from "../../../types/common";
import ErrorSlate from "../ErrorSlate";

interface LatLong {
  latitude: number;
  longitude: number;
}
interface MapFormComponentProps {
  form?: any;
  required?: boolean;
  error: TError;
  updateFormValue?(field_name: string, value: number | string | string[]): void;
  updateFormValues?: any;
}

const MapFormComponent = ({
  form,
  updateFormValues,
  updateFormValue,
  required,
  error,
}: MapFormComponentProps) => {
  const [latlong, setLatLong] = useState<LatLong>({
    latitude: form?.latitude | 1.3733,
    longitude: form?.longitude | 32.2903,
  });
  const [mapErrors, setMapErrors] = useState<TError>({});

  const mapRef = useRef<MapRef>(null);

  const handleMapClick = (event: any) => {
    const { lngLat } = event;
    setLatLong({
      latitude: lngLat.lat,
      longitude: lngLat.lng,
    });
    if (updateFormValues) {
      updateFormValues({
        latitude: lngLat.lat,
        longitude: lngLat.lng,
      });
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const floatValue = parseFloat(value);

    if (value === "" || isNaN(parseFloat(value))) {
      if (updateFormValue) {
        updateFormValue(name, "");
      }
      return;
    }

    if (name === "latitude" && (floatValue < -90 || floatValue > 90)) {
      setMapErrors((prevErrors) => ({
        ...prevErrors,
        latitude: "Invalid latitude value: must be between -90 and 90",
      }));
      return;
    }

    if (name === "longitude" && (floatValue < -180 || floatValue > 180)) {
      setMapErrors((prevErrors) => ({
        ...prevErrors,
        longitude: "Invalid longitude value: must be between -180 and 180",
      }));
      return;
    }

    setMapErrors((prevErrors) => ({
      ...prevErrors,
      [name]: undefined,
    }));

    setLatLong((prevState) => ({
      ...prevState,
      [name]: floatValue,
    }));

    if (updateFormValue) {
      updateFormValue(name, parseFloat(value));
    }
  };

  useEffect(() => {
    if (mapRef.current && latlong.latitude && latlong.longitude) {
      mapRef.current.flyTo({ center: [latlong.longitude, latlong.latitude] });
      // const geocoder = new MapboxGeocoder({
      //   accessToken: MAPBOX_ACCESS_TOKEN,
      //   mapboxgl: mapRef.current.getMap(),
      // });
      // mapRef.current.getMap().addControl(geocoder);
    }
  }, [latlong]);

  return (
    <div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gridGap: "1rem",
          marginBottom: "1rem",
        }}
      >
        <Wrapper>
          <p className="capitalize font12 mb-1">
            Latitude <RequiredIndicator required={required} />
          </p>
          {error && error.latitude && typeof error.latitude === "string" && (
            <ErrorSlate message={error.latitude} />
          )}
          {mapErrors &&
            mapErrors.latitude &&
            typeof mapErrors.latitude === "string" && (
              <ErrorSlate message={mapErrors.latitude} />
            )}
          <CFormInput
            size="sm"
            onChange={handleInputChange}
            name="latitude"
            placeholder="Enter Latitude"
            value={form?.latitude || ""}
            type={"number"}
            required={required}
          />
        </Wrapper>
        <Wrapper>
          <p className="capitalize font12 mb-1">
            Latitude <RequiredIndicator required={required} />
          </p>
          {error && error.longitude && typeof error.longitude === "string" && (
            <ErrorSlate message={error.longitude} />
          )}
          {mapErrors &&
            mapErrors.longitude &&
            typeof mapErrors.longitude === "string" && (
              <ErrorSlate message={mapErrors.longitude} />
            )}

          <CFormInput
            size="sm"
            onChange={handleInputChange}
            name="longitude"
            placeholder="Enter Longitude"
            value={form?.longitude || ""}
            type={"number"}
            required={required}
          />
        </Wrapper>
      </div>

      <Map
        ref={mapRef}
        initialViewState={{
          latitude: latlong.latitude,
          longitude: latlong.longitude,
          zoom: 8,
        }}
        style={{ width: "100%", height: "300px", borderRadius: "5px" }}
        mapStyle="mapbox://styles/mapbox/streets-v11"
        mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
        onClick={handleMapClick}
        attributionControl={false}
      >
        <Marker latitude={latlong.latitude} longitude={latlong.longitude} />
        <NavigationControl position="top-right" />
        <FullscreenControl position="top-right" />
      </Map>
    </div>
  );
};

export default MapFormComponent;
